/**
 * Canonical builders for Firebase Storage object paths.
 *
 * All uploaded files live under `accounts/{accountId}/...` so that Storage
 * security rules can enforce tenant isolation by account. The old flat
 * top-level paths (events/, visits/, cases/, parents/, children/,
 * supervisors/, reports/, exports/, documents/) are LEGACY — no code should
 * write to them anymore. A one-time migration copies existing legacy objects
 * into these account-scoped paths.
 *
 * Use these helpers everywhere a file is uploaded so writers cannot drift.
 */

const base = (accountId: string) => `accounts/${accountId}`;

export const storagePaths = {
  eventImage: (accountId: string, eventId: string, fileName: string) =>
    `${base(accountId)}/events/${eventId}/${fileName}`,

  visitEventReport: (accountId: string, visitId: string, fileName: string) =>
    `${base(accountId)}/visits/${visitId}/events/${fileName}`,

  visitPayment: (accountId: string, visitId: string, fileName: string) =>
    `${base(accountId)}/visits/${visitId}/payment/${fileName}`,

  reportPdf: (accountId: string, reportId: string, fileName: string) =>
    `${base(accountId)}/reports/${reportId}/${fileName}`,

  export: (accountId: string, caseId: string, fileName: string) =>
    `${base(accountId)}/exports/${caseId}/${fileName}`,

  caseFile: (accountId: string, caseId: string, fileName: string) =>
    `${base(accountId)}/cases/${caseId}/${fileName}`,

  parentFile: (accountId: string, parentId: string, fileName: string) =>
    `${base(accountId)}/parents/${parentId}/${fileName}`,

  parentAvatar: (accountId: string, parentId: string, fileName: string) =>
    `${base(accountId)}/parents/${parentId}/avatar/${fileName}`,

  childFile: (accountId: string, childId: string, fileName: string) =>
    `${base(accountId)}/children/${childId}/${fileName}`,

  childAvatar: (accountId: string, childId: string, fileName: string) =>
    `${base(accountId)}/children/${childId}/avatar/${fileName}`,

  supervisorFile: (accountId: string, supervisorId: string, fileName: string) =>
    `${base(accountId)}/supervisors/${supervisorId}/${fileName}`,

  supervisorAvatar: (accountId: string, supervisorId: string, fileName: string) =>
    `${base(accountId)}/supervisors/${supervisorId}/avatar/${fileName}`,

  documentFile: (accountId: string, documentId: string, fileName: string) =>
    `${base(accountId)}/documents/${documentId}/${fileName}`,
};

/**
 * The legacy top-level path prefixes, in migration order. Used by the
 * one-time backfill script to enumerate and relocate existing objects.
 */
export const LEGACY_STORAGE_PREFIXES = [
  'events',
  'visits',
  'reports',
  'exports',
  'cases',
  'parents',
  'children',
  'supervisors',
  'documents',
] as const;
